import axios, { AxiosResponse } from 'axios';
import { ExportType, getFileTypeFromExtension } from './fileProcessing';
import { FileType } from '../components/ExamResultsRepository/ExamResultsRepository.types';
import * as pako from 'pako';

export function addParam(paramName: string, paramValue?: string | boolean | number, dontAddForLengthLessThen?: number) {
  if (
    dontAddForLengthLessThen !== undefined &&
    typeof paramValue === 'string' &&
    paramValue.length < dontAddForLengthLessThen
  ) {
    return '';
  } else {
    return paramValue || paramValue === 0 ? `&${paramName}=${paramValue.toString().replace('%', '%25')}` : '';
  }
}

export function splitParams(paramName: string, paramValue: string | undefined) {
  return (paramValue ? paramValue.split(',') : []).map((item: string) => addParam(paramName, item)).join('');
}

export function isNonEmptyString (value: string | null | undefined): value is string  {
  return typeof value === 'string' && value.trim().length > 0;
}

export function splitTrueParams(paramName: string) {
  return paramName
    .split(',')
    .filter((item: string) => item.length > 0)
    .map((item: string) => addParam(item, 'true'))
    .join('');
}

export function addParamsList(paramName: string, paramValue?: string[] | number[]) {
  return paramValue && paramValue.length > 0
    ? paramValue.map((item: string | boolean | number | undefined) => addParam(paramName, item)).join('')
    : [];
}

/**
 * Axios transformResponse that preserves large 64-bit integer fields as strings
 * before JSON.parse rounds them (JS safe integer limit is 2^53-1).
 * The regex quotes bare numeric values for every field whose name ends with
 * "VersionId" or "LatestVersionId".
 */
export function transformResponsePreserveBigInts(data: string): unknown {
  if (typeof data !== 'string') return data;
  const quoted = data.replace(
    /("(?:examTaskGroupSolutionScoreVersionId|examinerLatestVersionId|verifierLatestVersionId|verifierExamTaskGroupSolutionScoreVerionId|examinerExamTaskGroupSolutionScoreVerionId)"\s*:\s*)(\d{16,})/g,
    '$1"$2"',
  );
  return JSON.parse(quoted);
}

export function formatDigit(input: number) {
  const value = input.toString();
  return value.length === 1 ? '0' + value : value;
}

export async function downloadFile(url: string, fileName: string) {
  await axios({
    url: url,
    method: 'GET',
    responseType: 'blob',
  }).then((response) => {
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
  });
}

export function exportFile(response: Blob, type?: ExportType | string, fileType?: FileType, customFileName?: string) {
  let url = '';
  if (fileType && fileType === FileType.json) {
    url = window.URL.createObjectURL(new Blob([JSON.stringify(response)]));
  } else {
    url = window.URL.createObjectURL(new Blob(['\ufeff' + response], { type: 'text/csv;charset=utf-8' }));
  }
  const link = document.createElement('a');
  link.href = url;
  const date = new Date();
  const formattedDate = `${date.getFullYear()}-${formatDigit(date.getMonth() + 1)}-${formatDigit(date.getDate())}_${formatDigit(
    date.getHours()
  )}_${formatDigit(date.getMinutes())}`;

  const fileExtension = fileType && fileType === FileType.json ? 'json' : 'csv';
  const fileName = customFileName ? `${customFileName}.${fileExtension}` : `${type}_${formattedDate}.${fileExtension}`;
  link.setAttribute('download', fileName);
  document.body.appendChild(link);
  link.click();
}

export async function dataUrlToFile(dataUrl: string, fileName: string): Promise<File> {
  const res: Response = await fetch(dataUrl);
  const blob: Blob = await res.blob();
  const mimeRegex = new RegExp(/data:(.*);base64/gi);
  const regexRes = mimeRegex.exec(dataUrl);
  const type = regexRes && regexRes.length >= 2 ? regexRes[1] : 'image/png';
  return new File([blob], fileName, { type });
}

/**
 * Compresses an array of recipient IDs using gzip compression and returns as binary Blob.
 * This reduces the size of large ID lists when sending them to the server.
 * 
 * @param ids - Array of string IDs to compress
 * @returns Blob containing compressed binary data
 */
export function compressRecipientIds(ids: string[]): Blob {
  // Convert IDs to binary (8 bytes per long)
  const buffer = new ArrayBuffer(ids.length * 8);
  const view = new DataView(buffer);

  ids.forEach((id, index) => {
    view.setBigInt64(index * 8, BigInt(id), true); // little-endian
  });

  // Compress with gzip
  const compressed = pako.gzip(new Uint8Array(buffer));

  // Create blob from compressed data
  return new Blob([compressed], { type: 'application/octet-stream' });
}
